import { createContext, useContext, useState, useMemo, useEffect, ReactNode } from "react";
import {
  DEFAULT_BRAND,
  isFontPreset,
  isRadiusPreset,
  isDensityPreset,
  isUnitsPreset,
  isLocale,
  isValidHex,
  type FontPreset,
  type RadiusPreset,
  type DensityPreset,
  type UnitsPreset,
} from "@/lib/brand";

interface EmbedPrefsState {
  theme: "light" | "dark";
  setTheme: (v: "light" | "dark") => void;
  accent: string;
  setAccent: (v: string) => void;
  defaultCurrency: string;
  setDefaultCurrency: (v: string) => void;

  // Phase 2, BrandConfig fields
  accentHex: string | null;
  setAccentHex: (v: string | null) => void;
  surfaceHex: string | null;
  setSurfaceHex: (v: string | null) => void;
  font: FontPreset;
  setFont: (v: FontPreset) => void;
  radius: RadiusPreset;
  setRadius: (v: RadiusPreset) => void;
  density: DensityPreset;
  setDensity: (v: DensityPreset) => void;
  locale: string;
  setLocale: (v: string) => void;
  units: UnitsPreset;
  setUnits: (v: UnitsPreset) => void;
  resetBranding: () => void;

  /** Whether the current tool page has unsaved configurator edits (e.g. modified FAQ items). */
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
}

const EmbedPrefsContext = createContext<EmbedPrefsState | null>(null);

const STORAGE_KEY = "embedkit:prefs:v3";
const SCHEMA_VERSION = 3;

interface PersistedState {
  theme: "light" | "dark";
  accent: string;
  defaultCurrency: string;
  accentHex: string | null;
  surfaceHex: string | null;
  font: FontPreset;
  radius: RadiusPreset;
  density: DensityPreset;
  locale: string;
  units: UnitsPreset;
}

const DEFAULTS: PersistedState = {
  theme: "light",
  accent: "emerald",
  defaultCurrency: "AUD",
  accentHex: null,
  surfaceHex: null,
  font: DEFAULT_BRAND.font,
  radius: DEFAULT_BRAND.radius,
  density: DEFAULT_BRAND.density,
  locale: DEFAULT_BRAND.locale,
  units: DEFAULT_BRAND.units,
};

function readInitial(): PersistedState {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== SCHEMA_VERSION) return DEFAULTS;
    return {
      theme: parsed.theme === "dark" ? "dark" : "light",
      accent: typeof parsed.accent === "string" ? parsed.accent : DEFAULTS.accent,
      defaultCurrency:
        typeof parsed.defaultCurrency === "string" ? parsed.defaultCurrency : DEFAULTS.defaultCurrency,
      accentHex: isValidHex(parsed.accentHex) ? parsed.accentHex : null,
      surfaceHex: isValidHex(parsed.surfaceHex) ? parsed.surfaceHex : null,
      font: isFontPreset(parsed.font) ? parsed.font : DEFAULTS.font,
      radius: isRadiusPreset(parsed.radius) ? parsed.radius : DEFAULTS.radius,
      density: isDensityPreset(parsed.density) ? parsed.density : DEFAULTS.density,
      locale: isLocale(parsed.locale) ? parsed.locale : DEFAULTS.locale,
      units: isUnitsPreset(parsed.units) ? parsed.units : DEFAULTS.units,
    };
  } catch {
    return DEFAULTS;
  }
}

/**
 * Persists embed preferences (theme, accent, currency + Phase 2 BrandConfig
 * fields) across tool navigation within the dashboard, and exposes a dirty
 * flag that EmbedConfigurator uses with React Router's `useBlocker` to
 * intercept navigation when there are unsaved FAQ edits.
 *
 * Mirrored to localStorage (`embedkit:prefs:v3`). Old payloads (v1/v2) are
 * discarded safely and reset to defaults.
 */
export function EmbedPrefsProvider({ children }: { children: ReactNode }) {
  const initial = readInitial();
  const [theme, setTheme] = useState<"light" | "dark">(initial.theme);
  const [accent, setAccent] = useState(initial.accent);
  const [defaultCurrency, setDefaultCurrency] = useState(initial.defaultCurrency);
  const [accentHex, setAccentHex] = useState<string | null>(initial.accentHex);
  const [surfaceHex, setSurfaceHex] = useState<string | null>(initial.surfaceHex);
  const [font, setFont] = useState<FontPreset>(initial.font);
  const [radius, setRadius] = useState<RadiusPreset>(initial.radius);
  const [density, setDensity] = useState<DensityPreset>(initial.density);
  const [locale, setLocale] = useState(initial.locale);
  const [units, setUnits] = useState<UnitsPreset>(initial.units);
  const [isDirty, setDirty] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: SCHEMA_VERSION,
          theme, accent, defaultCurrency,
          accentHex, surfaceHex, font, radius, density, locale, units,
        }),
      );
    } catch {
      // ignore
    }
  }, [theme, accent, defaultCurrency, accentHex, surfaceHex, font, radius, density, locale, units]);

  const resetBranding = () => {
    setAccentHex(null);
    setSurfaceHex(null);
    setFont(DEFAULTS.font);
    setRadius(DEFAULTS.radius);
    setDensity(DEFAULTS.density);
    setLocale(DEFAULTS.locale);
    setUnits(DEFAULTS.units);
  };

  const value = useMemo<EmbedPrefsState>(
    () => ({
      theme, setTheme,
      accent, setAccent,
      defaultCurrency, setDefaultCurrency,
      accentHex, setAccentHex,
      surfaceHex, setSurfaceHex,
      font, setFont,
      radius, setRadius,
      density, setDensity,
      locale, setLocale,
      units, setUnits,
      resetBranding,
      isDirty, setDirty,
    }),
    [theme, accent, defaultCurrency, accentHex, surfaceHex, font, radius, density, locale, units, isDirty],
  );

  return (
    <EmbedPrefsContext.Provider value={value}>
      {children}
    </EmbedPrefsContext.Provider>
  );
}

export function useEmbedPrefs(): EmbedPrefsState {
  const ctx = useContext(EmbedPrefsContext);
  if (!ctx) {
    throw new Error("useEmbedPrefs must be used within an EmbedPrefsProvider");
  }
  return ctx;
}
